/**
 * Hashtag → Syphon Scan Map
 *
 * Single source of truth mapping each curated hashtag to the Syphon scan
 * endpoint(s) it requires. Adding, removing, or modifying a hashtag's scans
 * is a config-only change — no logic edits needed.
 *
 * Scan names are verified against the Syphon `ScanNames` enum
 * (swagger.yaml @ master).
 *
 * Endpoint: GET /market/scans/{scanName}
 *
 * @see STORY-007 (gitlab.com/monex-securities/insight/popsicle/-/work_items/710)
 */

import HASHTAG_FILTER_CONFIG from './hashtagFilterConfig';

/**
 * Whitelist of all valid ScanNames enum values used across the hashtag map.
 * Used by unit tests to validate the map contains only real scan names.
 */
export const VALID_SCAN_NAMES = Object.freeze([
  'AnalystCount',
  'DividendPayoutRatio',
  'DividendYieldForecast',
  'Exchange',
  'GrowthScore',
  'IchimokuCloud',
  'InvestmentAmount',
  'InventoryTurnoverRate',
  'MarketCap',
  'NetSalesChange',
  'OrdinaryProfitToNetSalesRatio',
  'PredictedOrdinaryProfit',
  'PriceCashFlowRatio',
  'Rating',
  'ReceivablesTurnoverRate',
  'ReturnOnEquity',
  'SMADeviation_25',
  'SMACrossover_5_25',
  'SecurityType',
  'Sector',
  'StochasticFast_9',
  'TargetPriceVsCurrentPrice',
  'TotalScore',
]);

/**
 * Map from hashtag ID → array of Syphon scan names.
 *
 * DERIVED from HASHTAG_FILTER_CONFIG so this fetch list and the loading/error
 * gate (getRequiredScans over the same config) can never drift — the config is
 * the single source of truth for which scans a hashtag needs. Each entry is the
 * hashtag's condition `scanName`s, de-duplicated in declaration order.
 *
 * Keys correspond to the `id` field in HASHTAG_DEFINITIONS
 * (src/views/scan/HashtagChips.js).
 */
const HASHTAG_SCAN_MAP = Object.freeze(
  Object.fromEntries(
    Object.entries(HASHTAG_FILTER_CONFIG).map(([id, conditions]) => [
      id,
      Object.freeze([...new Set(conditions.map((c) => c.scanName))]),
    ])
  )
);

export default HASHTAG_SCAN_MAP;

/**
 * Collect unique scan names for the given set of selected hashtag IDs.
 * De-duplicates scans shared across multiple selected hashtags so each
 * scan fires only once per selection cycle.
 *
 * @param {Set<string>|Array<string>} selectedIds - The currently selected hashtag IDs.
 * @returns {string[]} De-duplicated array of scan names.
 */
export function getScansForSelection(selectedIds) {
  const ids = selectedIds instanceof Set ? selectedIds : new Set(selectedIds);
  const scans = new Set();
  ids.forEach((id) => {
    const mapped = HASHTAG_SCAN_MAP[id];
    if (mapped) {
      mapped.forEach((scan) => scans.add(scan));
    }
  });
  return Array.from(scans);
}
